import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, STEPPER, SURFACE } from "../../../theme/tokens";

export type SystemStepperSize = "standard" | "large" | "compact" | "inline";

/** Whether the − and + always show, or wait for a hover or focus (see `SystemStepper`'s `controlsVisibility`). */
export type SystemStepperControlsVisibility = "always" | "reveal";

/** The number's ink: `neutral` is the page's ink, `brand` the brand's dark (a minimum read a step quieter than its target). */
export type SystemStepperValueTone = "neutral" | "brand";

/**
 * Put this class on an ancestor — a grid row, a list item — to reveal the − and + of every `controlsVisibility="reveal"`
 * stepper inside it while that ancestor is hovered or holds focus, so a row's steppers appear together as the pointer
 * enters the row, not one at a time as it reaches each number. Nothing else changes: the class is a static marker, and
 * the stepper owns the reveal styles (`.{class}:hover <stepper>` / `.{class}:focus-within <stepper>`).
 */
export const SYSTEM_STEPPER_REVEAL_CLASS = "SystemStepper-revealScope";

/** The field sizes: a bordered box with the − and + inside it, rather than round buttons beside the number. */
const isFieldSize = (size: SystemStepperSize): boolean => size === "compact" || size === "inline";

/** Between each button and the value (px). The field sizes have none — the buttons are the field's ends. */
export const stepperGapPx = (size: SystemStepperSize): number => (size === "inline" ? 0 : STEPPER.GAP_PX[size]);

/** The round − and + of the standard and large steppers: `TOUCH_TARGET_MIN` squares on a neutral fill. */
const ROUND_BUTTON_SX = {
	flex: "none",
	width: STEPPER.BUTTON_SIZE,
	height: STEPPER.BUTTON_SIZE,
	display: "grid",
	placeItems: "center",
	p: 0,
	border: 0,
	borderRadius: "50%",
	bgcolor: NEUTRAL[150],
	color: INK.PRIMARY,
	cursor: "pointer",
	WebkitTapHighlightColor: "transparent",
	transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
	"@media (hover: hover) and (pointer: fine)": { "&:hover:not(:disabled)": { bgcolor: NEUTRAL[200] } },
	"&:active:not(:disabled)": { transform: "scale(0.94)" },
	"&:disabled": { cursor: "default", color: INK.DISABLED, bgcolor: NEUTRAL[50] },
	"& svg": { fontSize: ICON_SIZE.MENU_ROW },
	"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
} as const;

/**
 * The compact stepper is one field: a hairline box the height of a compact control, which takes the brand's border
 * and focus ring while anything in it has focus, as a text field does.
 */
const compactFieldSx = (theme: Theme) =>
	({
		boxSizing: "border-box",
		height: STEPPER.COMPACT_HEIGHT,
		border: `${STEPPER.COMPACT_BORDER}px solid ${NEUTRAL[200]}`,
		borderRadius: 1,
		bgcolor: SURFACE,
		overflow: "hidden",
		transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
		"&:focus-within": { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	}) as const;

/**
 * The inline stepper: a hairline box `CONTROL_HEIGHT.MICRO` tall that fits a dense row line. It takes the brand's
 * border and a thin ring only while its VALUE has keyboard focus — a click on − or + focuses that button, and a ring
 * left behind on every row a pointer touched would be noise down a table.
 */
const inlineFieldSx = (theme: Theme) =>
	({
		boxSizing: "border-box",
		height: STEPPER.INLINE_HEIGHT,
		border: `${STEPPER.INLINE_BORDER}px solid ${NEUTRAL[200]}`,
		borderRadius: 1,
		bgcolor: SURFACE,
		overflow: "hidden",
		transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
		"&:has([role='spinbutton']:focus-visible)": {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 ${STEPPER.INLINE_FOCUS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`,
		},
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	}) as const;

/** The field look of a size: the compact and inline boxes; the round sizes have none. */
export const fieldSx = (size: SystemStepperSize, theme: Theme) => (size === "compact" ? compactFieldSx(theme) : size === "inline" ? inlineFieldSx(theme) : {});

/** A field stepper's − and +: the field's full inner height, plain until hovered, then the brand's light wash. */
const fieldButtonSx = (theme: Theme, width: number) =>
	({
		flex: "none",
		alignSelf: "stretch",
		width,
		display: "grid",
		placeItems: "center",
		p: 0,
		border: 0,
		bgcolor: "transparent",
		color: INK.MUTED,
		cursor: "pointer",
		WebkitTapHighlightColor: "transparent",
		transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover:not(:disabled)": { bgcolor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY), color: theme.palette.primary.dark },
		},
		"&:active:not(:disabled)": { transform: "scale(0.94)" },
		"&:disabled": { cursor: "default", color: INK.DISABLED },
		"& svg": { fontSize: ICON_SIZE.MICRO },
		"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
	}) as const;

/** The − and + of a size. */
export const buttonSx = (size: SystemStepperSize, theme: Theme) =>
	size === "compact"
		? fieldButtonSx(theme, STEPPER.COMPACT_BUTTON_WIDTH)
		: size === "inline"
			? fieldButtonSx(theme, STEPPER.INLINE_BUTTON_WIDTH)
			: ROUND_BUTTON_SX;

const VALUE_FONT_SIZE: Record<SystemStepperSize, string> = {
	standard: FONT_SIZE.ROW_LABEL,
	large: FONT_SIZE.STAT_VALUE,
	compact: FONT_SIZE.EMPHASIS,
	inline: STEPPER.INLINE_FONT_SIZE,
};

/** The number — the spinbutton itself. */
export const valueSx = (size: SystemStepperSize, tone: SystemStepperValueTone, theme: Theme) => ({
	minWidth: size === "inline" ? STEPPER.INLINE_VALUE_MIN_WIDTH : STEPPER.VALUE_MIN_WIDTH[size],
	textAlign: "center",
	fontSize: VALUE_FONT_SIZE[size],
	// A field's figure is one among several in a form or row, so it reads at that surface's weight, not as a headline.
	fontWeight: isFieldSize(size) ? 600 : 800,
	lineHeight: 1.1,
	letterSpacing: LETTER_SPACING.TIGHT_FIGURE,
	fontVariantNumeric: "tabular-nums",
	color: tone === "brand" ? theme.palette.primary.dark : INK.PRIMARY,
	borderRadius: 1,
	outline: "none",
	// A field draws its own focus ring around the whole box; a round stepper rings the number.
	"&:focus-visible": { boxShadow: isFieldSize(size) ? "none" : `0 0 0 2px ${theme.palette.primary.main}` },
});

/** The reveal's own transitions: the buttons fade, the field's box fills in. Dropped under reduced motion — the controls just appear. */
const REVEAL_BUTTON_TRANSITION = ["opacity", "background-color", "color", "transform"]
	.map((property) => `${property} ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`)
	.join(", ");
const REVEAL_FIELD_TRANSITION = ["border-color", "box-shadow", "background-color"]
	.map((property) => `${property} ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`)
	.join(", ");

const SCOPE = `.${SYSTEM_STEPPER_REVEAL_CLASS}`;

/**
 * `controlsVisibility="reveal"`, applied to the root after the field look. On a fine pointer that can hover, the − and +
 * are invisible and unclickable (they keep their width, so the number never moves) and a field's box is clear, until
 * the stepper or a `SYSTEM_STEPPER_REVEAL_CLASS` ancestor is hovered or holds focus. A touch screen can't hover, so
 * there the controls always show.
 *
 * The box rules skip a stepper that holds focus (`:not(:focus-within)`), so a field's own focus look — the compact
 * field's brand border, the inline field's keyboard ring — is never overridden by the reveal.
 */
export const revealSx = (size: SystemStepperSize) => {
	const field = isFieldSize(size);
	const shown = { opacity: 1, pointerEvents: "auto" } as const;
	return {
		...(field ? { transition: REVEAL_FIELD_TRANSITION } : {}),
		"& > button": { transition: REVEAL_BUTTON_TRANSITION },
		"@media (hover: hover) and (pointer: fine)": {
			...(field ? { "&:not(:focus-within)": { borderColor: "transparent", bgcolor: "transparent" } } : {}),
			...(field
				? {
						[`&:hover:not(:focus-within), ${SCOPE}:hover &:not(:focus-within), ${SCOPE}:focus-within &:not(:focus-within)`]: {
							borderColor: NEUTRAL[200],
							bgcolor: SURFACE,
						},
					}
				: {}),
			"& > button": { opacity: 0, pointerEvents: "none" },
			[`&:hover > button, &:focus-within > button, ${SCOPE}:hover & > button, ${SCOPE}:focus-within & > button`]: shown,
		},
		"@media (prefers-reduced-motion: reduce)": { transition: "none", "& > button": { transition: "none" } },
	};
};
